import { API_BASE_URL } from './api';
import { attributionLandingVariant } from './landing-experiment';

const SESSION_KEY = 'attribution_fingerprint_sent';
const STORED_UTM_KEY = 'harbor_web_attribution';
const MATCH_ATTEMPTED_KEY = 'harbor_web_attribution_matched';

/**
 * Align with the server IP-match window (7 days). Fingerprint rows are never
 * TTL-deleted; matching only looks back 7 days (IP/eclid) or 30 minutes
 * (global fallback, click-id only). Web Clerk signup is same-origin and
 * typically minutes after the satellite→homepage visit, so the fingerprint
 * still exists at upsert. localStorage is a first-touch re-stamp if /match
 * misses (VPN / IP change) — not a backfill of historical rows.
 */
const STORED_UTM_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type WebAttributionPayload = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  rdt_cid?: string;
  eclid?: string;
  landing_variant?: string;
};

type StoredWebAttribution = WebAttributionPayload & { captured_at: number };

type GetToken = () => Promise<string | null>;

function compactPayload(input: Record<string, string | undefined>): WebAttributionPayload {
  const payload: WebAttributionPayload = {};
  (Object.keys(input) as (keyof WebAttributionPayload)[]).forEach((key) => {
    const value = input[key];
    if (value) payload[key] = value;
  });
  return payload;
}

function hasAttributionSignal(payload: WebAttributionPayload | null | undefined): boolean {
  if (!payload) return false;
  return Boolean(
    payload.utm_source ||
      payload.utm_medium ||
      payload.utm_campaign ||
      payload.utm_content ||
      payload.utm_term ||
      payload.gclid ||
      payload.fbclid ||
      payload.ttclid ||
      payload.rdt_cid ||
      payload.eclid
  );
}

function readUrlAttribution(): WebAttributionPayload {
  const params = new URLSearchParams(window.location.search);
  return compactPayload({
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    utm_term: params.get('utm_term') || undefined,
    gclid: params.get('gclid') || undefined,
    fbclid: params.get('fbclid') || undefined,
    ttclid: params.get('ttclid') || undefined,
    rdt_cid: params.get('rdt_cid') || undefined,
    eclid: params.get('eclid') || undefined,
  });
}

function persistAttribution(payload: WebAttributionPayload): void {
  if (!hasAttributionSignal(payload)) return;
  try {
    const existing = readStoredAttribution();
    if (existing) return; // first-touch
    const stored: StoredWebAttribution = {
      ...payload,
      captured_at: Date.now(),
    };
    localStorage.setItem(STORED_UTM_KEY, JSON.stringify(stored));
  } catch {
    // Safari private mode / quota — fingerprint POST is the primary path.
  }
}

export function readStoredAttribution(): WebAttributionPayload | null {
  try {
    const raw = localStorage.getItem(STORED_UTM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredWebAttribution;
    if (
      typeof parsed.captured_at !== 'number' ||
      Date.now() - parsed.captured_at > STORED_UTM_MAX_AGE_MS
    ) {
      localStorage.removeItem(STORED_UTM_KEY);
      return null;
    }
    const { captured_at: _capturedAt, ...payload } = parsed;
    return hasAttributionSignal(payload) ? payload : null;
  } catch {
    return null;
  }
}

/**
 * Ad attribution for an analytics event: this page's URL params when they
 * carry a signal (last touch), else the stored first-touch params.
 * ``eclid`` is excluded because it embeds a prospect id.
 */
export function currentAttributionProperties(): Record<string, string> {
  const fromUrl = readUrlAttribution();
  const payload = hasAttributionSignal(fromUrl) ? fromUrl : readStoredAttribution();
  if (!payload) return {};
  const { eclid: _eclid, ...rest } = payload;
  return compactPayload(rest) as Record<string, string>;
}

function clearStoredAttribution(): void {
  try {
    localStorage.removeItem(STORED_UTM_KEY);
  } catch {
    // ignore
  }
}

function matchSucceeded(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const row = data as WebAttributionPayload & { source?: string };
  return Boolean(
    row.source ||
      row.utm_source ||
      row.utm_medium ||
      row.gclid ||
      row.fbclid ||
      row.ttclid ||
      row.rdt_cid ||
      row.eclid
  );
}

/**
 * Captures UTM params, click ids (gclid, fbclid, ttclid, rdt_cid), and prospect ``eclid``
 * from the URL, then POSTs a fingerprint to the backend for later matching
 * with mobile app signups. Fires once per session, fails silently.
 *
 * Also first-touch persists UTMs in localStorage so a later Clerk web signup
 * can /match or re-stamp them even after the query string is gone.
 */
export function captureAttributionFingerprint(): void {
  const fromUrl = readUrlAttribution();
  const landingVariant = attributionLandingVariant();
  const withVariant: WebAttributionPayload = landingVariant
    ? { ...fromUrl, landing_variant: landingVariant }
    : fromUrl;
  persistAttribution(withVariant);

  if (sessionStorage.getItem(SESSION_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const utm_source = params.get('utm_source');
  const utm_medium = params.get('utm_medium');
  const utm_campaign = params.get('utm_campaign');
  const utm_content = params.get('utm_content');
  const utm_term = params.get('utm_term');
  const gclid = params.get('gclid');
  const fbclid = params.get('fbclid');
  const ttclid = params.get('ttclid');
  const rdt_cid = params.get('rdt_cid');
  const eclid = params.get('eclid');
  const landingPath = window.location.pathname;
  const referrer = document.referrer || undefined;

  const excludedPrefixes = ['/admin', '/app', '/auth', '/purchase', '/v1'];
  const isMarketingPath = !excludedPrefixes.some((prefix) => landingPath.startsWith(prefix));

  // Capture if there are attribution params OR a marketing landing path/referrer.
  if (
    !isMarketingPath &&
    !utm_source &&
    !utm_medium &&
    !utm_campaign &&
    !utm_content &&
    !utm_term &&
    !gclid &&
    !fbclid &&
    !ttclid &&
    !rdt_cid &&
    !eclid
  ) {
    return;
  }

  sessionStorage.setItem(SESSION_KEY, '1');

  const body: Record<string, string | number> = {
    screen_width: window.screen.width,
    screen_height: window.screen.height,
  };

  if (utm_source) body.utm_source = utm_source;
  if (utm_medium) body.utm_medium = utm_medium;
  if (utm_campaign) body.utm_campaign = utm_campaign;
  if (utm_content) body.utm_content = utm_content;
  if (utm_term) body.utm_term = utm_term;
  if (gclid) body.gclid = gclid;
  if (fbclid) body.fbclid = fbclid;
  if (ttclid) body.ttclid = ttclid;
  if (rdt_cid) body.rdt_cid = rdt_cid;
  if (eclid) body.eclid = eclid;
  if (isMarketingPath) body.landing_path = landingPath;
  if (isMarketingPath && referrer) body.referrer = referrer;
  if (isMarketingPath && landingVariant) body.landing_variant = landingVariant;

  fetch(`${API_BASE_URL}/v1/attribution/fingerprint`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => {
    // Fire-and-forget — silently ignore errors
  });
}

/**
 * After Clerk web signup/session is active, claim the homepage fingerprint
 * the same way iOS does (GET /v1/attribution/match). require_current_user
 * upserts the Harbor user on that request, so match runs at upsert time
 * from the same desktop IP.
 *
 * If /match returns empty (no IP hit) but this browser still has first-touch
 * UTMs, re-stamp them via POST /v1/attribution/record (source=web_utm).
 */
export async function matchWebAttribution(getToken: GetToken): Promise<void> {
  if (sessionStorage.getItem(MATCH_ATTEMPTED_KEY)) return;

  const token = await getToken();
  if (!token) return;

  const stored = readStoredAttribution() || readUrlAttribution();
  const matchUrl = stored.eclid
    ? `${API_BASE_URL}/v1/attribution/match?eclid=${encodeURIComponent(stored.eclid)}`
    : `${API_BASE_URL}/v1/attribution/match`;

  try {
    const res = await fetch(matchUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    if (!res.ok) return;

    const data: unknown = await res.json();
    if (matchSucceeded(data)) {
      sessionStorage.setItem(MATCH_ATTEMPTED_KEY, '1');
      clearStoredAttribution();
      return;
    }

    if (!hasAttributionSignal(stored)) {
      sessionStorage.setItem(MATCH_ATTEMPTED_KEY, '1');
      return;
    }

    const recordRes = await fetch(`${API_BASE_URL}/v1/attribution/record`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        source: 'web_utm',
        ...stored,
      }),
    });
    if (!recordRes.ok) return;

    sessionStorage.setItem(MATCH_ATTEMPTED_KEY, '1');
    clearStoredAttribution();
  } catch {
    // Leave the flag unset so a later signed-in mount can retry.
  }
}
